import { Fragment, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

import { CircleAlert, CircleCheck, PenIcon, View } from 'lucide-react';
import {
  PlannerConfigEntityWithOptionalFields,
  PlannerConfigMarketDataEntity,
} from '@/services/configurator/PlannerConfigTypes';
import {
  REGEX_PATTERN_NUMBER_OR_STRING,
  REGEX_PATTERN_NUMBER,
} from '@/utils/regex';
import { Input } from '@/components/ui/input';
import { isProdTabActive } from '@/shared-utils/utils';
import { useEnv } from '@/components/providers/env-provider';
import { ControllerRenderProps, UseFormReturn } from 'react-hook-form';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  formFields,
  formFieldsArray,
  handleChange,
} from './planner-form-market-data-dialog-utils';
import { Checkbox } from '@/components/ui/checkbox';
import FormPillInputComponent, {
  InputFields,
} from '@/app/form/formComponent/PillInput/FormPillInputComponent';
import FormCheckboxComponent, {
  CheckboxFields,
} from '@/app/form/formComponent/FormCheckboxComponent';

export type PlannerFormItemMarketDataDialogProps = {
  marketData?: PlannerConfigMarketDataEntity[] | null;
  onMarketDataChange: (marketData: PlannerConfigMarketDataEntity[]) => void;
  data?: PlannerConfigMarketDataEntity;
  disabled?: boolean;
  disableIdFields?: boolean;
  currentMarket: string;
  form: UseFormReturn<PlannerConfigEntityWithOptionalFields>;
  field: ControllerRenderProps<
    PlannerConfigEntityWithOptionalFields,
    'marketData'
  >;
  resetFormErrors: (marketIndex: number) => void;
  hasMarketOverrides: (data: PlannerConfigMarketDataEntity) => boolean;
};

export function PlannerFormItemMarketDataDialog(
  props: PlannerFormItemMarketDataDialogProps,
) {
  const disableIdFields = props.disabled || props.disableIdFields;
  const { env } = useEnv();
  const isProd = isProdTabActive(env);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);

  const [open, setOpen] = useState(false);
  const [hasExited, setHasExited] = useState(false);

  if (!props?.marketData) {
    return null;
  }

  const marketIndex = props.marketData.findIndex(
    (market) => market.locale === props.currentMarket,
  );

  const currentBannerMode = props.form.watch(
    `marketData.${marketIndex}.bannerLargeMode`,
  );

  const getIcon = (overrides: PlannerConfigMarketDataEntity) => {
    if (!overrides.isActive && hasError) {
      return (
        <CircleAlert
          className='flex items-center justify-center fill-rose-500 text-white'
          size={24}
        />
      );
    }
    if (!hasError && props.hasMarketOverrides(overrides)) {
      return (
        <CircleCheck
          className='flex items-center justify-center fill-lime-500 text-white'
          size={24}
        />
      );
    } else if (hasError) {
      return (
        <CircleAlert
          className='flex items-center justify-center fill-rose-500 text-white'
          size={24}
        />
      );
    }
  };

  const handleConfirmedExit = () => {
    props.resetFormErrors(marketIndex);
    setHasExited(true);

    setShowExitConfirmation(false);
    setOpen(false);
  };

  const hasError =
    props?.form?.formState.errors?.marketData &&
    props.form.formState.errors.marketData[marketIndex];
  const isActiveAndNotDisabled = props.data?.isActive && !props.disabled;
  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen && open && hasError && !hasExited) {
            setShowExitConfirmation(true);
            return;
          }
          setOpen(isOpen);
          if (isOpen) {
            setHasExited(false);
          }
        }}
      >
        <DialogTrigger asChild>
          <Button
            variant='ghost'
            className={`h-6 p-0 ${isActiveAndNotDisabled ? 'w-8' : 'w-0'}`}
          >
            {isActiveAndNotDisabled && props.data && (
              <PenIcon
                className={`h-4 w-4 ${props.hasMarketOverrides(props.data) ? 'ml-8' : 'ml-0'}`}
              />
            )}
            {props.data && (
              <div className={`${!isActiveAndNotDisabled ? 'ml-8' : 'ml-0'}`}>
                {getIcon(props.data)}
              </div>
            )}
          </Button>
        </DialogTrigger>
        <DialogContent
          className='min-w-[900px]'
          onEscapeKeyDown={(e) => {
            if (hasError && !hasExited) {
              e.preventDefault();
              setShowExitConfirmation(true);
            }
          }}
          onInteractOutside={(e) => {
            if (hasError && !hasExited) {
              e.preventDefault();
              setShowExitConfirmation(true);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Edit market data {props.currentMarket}</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            Make market specific overrides here
          </DialogDescription>
          <div className='grid gap-4 px-1 py-4 pe-4 max-h-96 overflow-y-scroll'>
            <FormCheckboxComponent
              formControl={props.form.control}
              name={
                `marketData.${marketIndex}.includeDeeplinkParams` as CheckboxFields
              }
              formLabel='Include Deeplink Params'
              formDescription='Whether to include deeplink parameters in the URL'
              disabled={props.disabled || isProd}
            />
            <FormField
              control={props.form.control}
              name={`marketData.${marketIndex}.bannerLargeMode`}
              render={({ field }) => (
                <FormItem>
                  <div className='flex flex-row items-start space-x-3 space-y-0'>
                    <FormControl>
                      <Checkbox
                        checked={field.value === 'VIDEO'}
                        onCheckedChange={(checked) => {
                          const newValue = checked ? 'VIDEO' : 'IMAGE';
                          handleChange({
                            fieldName: 'bannerLargeMode',
                            value: newValue,
                            field,
                            props,
                            marketIndex,
                          });
                        }}
                        disabled={props.disabled || isProd}
                      />
                    </FormControl>
                    <Label>Enable video banner mode for large banner</Label>
                  </div>
                  <FormDescription>
                    When enabled, the large banner will display a video instead
                    of an image
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {formFields.map((fieldItem) => {
              if (
                fieldItem.name === 'videoPosterUrl' &&
                currentBannerMode !== 'VIDEO'
              ) {
                return null;
              }
              return (
                <div key={fieldItem.name}>
                  <FormField
                    control={props.form.control}
                    name={`marketData.${marketIndex}.${fieldItem.name}`}
                    render={({ field }) => (
                      <>
                        <FormItem>
                          <Label className='text-left'>{fieldItem.label}</Label>
                          {field.value && fieldItem?.hasPreview && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger
                                  onClick={(e) => e.preventDefault()}
                                  asChild
                                >
                                  <View className='ml-2 h-4 w-4' />
                                </TooltipTrigger>
                                <TooltipContent>
                                  {(fieldItem.name === 'imageUrl' ||
                                    fieldItem.name ===
                                      'secondaryBannerImgUrl' ||
                                    fieldItem.name === 'videoPosterUrl') && (
                                    <img
                                      src={field.value as string}
                                      className='w-[200px]'
                                      alt=''
                                    />
                                  )}
                                  {fieldItem.name === 'videoUrl' && (
                                    <video
                                      src={field.value as string}
                                      className='w-[200px]'
                                      autoPlay
                                      muted
                                      loop
                                      playsInline
                                      controls
                                    />
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          <FormControl>
                            <Input
                              {...field}
                              value={(field.value as string) || ''}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                handleChange({
                                  fieldName:
                                    field.name as keyof PlannerConfigMarketDataEntity,
                                  value: newValue,
                                  field,
                                  props,
                                  marketIndex,
                                });
                              }}
                              disabled={props.disabled || isProd}
                            />
                          </FormControl>
                          <FormDescription>
                            {fieldItem?.description}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      </>
                    )}
                  />
                </div>
              );
            })}
            {formFieldsArray.map((fieldItemArray) => {
              const regExpPattern =
                fieldItemArray.name === 'productsInclude' ||
                fieldItemArray.name === 'productsExclude'
                  ? REGEX_PATTERN_NUMBER
                  : REGEX_PATTERN_NUMBER_OR_STRING;
              return (
                <Fragment key={fieldItemArray.name}>
                  <FormPillInputComponent
                    formControl={props.form.control}
                    name={
                      `marketData.${marketIndex}.${fieldItemArray.name}` as InputFields
                    }
                    regexpValidation={regExpPattern}
                    formLabel={fieldItemArray.label}
                    formDescription='What product planner should be active in'
                    placeholder='Enter product id'
                    disabled={disableIdFields!}
                  />
                </Fragment>
              );
            })}
          </div>
          <DialogFooter className='sm:justify-end'>
            <DialogClose asChild>
              <Button
                disabled={props.disabled || !!hasError}
                type='button'
                variant='default'
                onClick={() => {}}
              >
                Done
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={showExitConfirmation}
        onOpenChange={setShowExitConfirmation}
      >
        <DialogContent>
          <DialogHeader>
            Are you sure you wish to exit, The fields with errors will be
            cleared
          </DialogHeader>
          <DialogFooter>
            <div className='flex items-center content-end'>
              <Button
                variant='default'
                className='h-6 w-8 py-4 px-8 mr-1'
                onClick={handleConfirmedExit}
              >
                Yes
              </Button>
              <Button
                variant='secondary'
                className='h-6 w-8 py-4 px-8'
                onClick={() => setShowExitConfirmation(false)}
              >
                No
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
